<script setup lang="ts">
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";

import type { PaginationParams } from "@/types";
import TemplateItem from "@/components/report-templates/TemplateItem.vue";
import { useGetReportTemplates } from "@/stores/queries/report-templates.query";
import type { ReportTemplate } from "@/types/report-templates.types";
import TemplateDrawer from "./TemplateDrawer.vue";
import DRBtn from "../UI/DRBtn.vue";
import ImportTemplateDialog from "../diagnostics/ImportTemplateDialog.vue";
import CreateTemplateReportBanner from "../diagnostics/CreateTemplateReportBanner.vue";
import TemplateDefaultSettingsDrawer from "./TemplateDefaultSettingsDrawer.vue";
import DRDisplayList from "../UI/items/DRDisplayList.vue";
import OrganizationPageLayout from "@/components/organization/OrganizationPageLayout.vue";

const { t } = useTranslation();

const page = ref(1);
const rowsPerPage = 12;
const sortBy = ref<"createdAt" | "name" | "status">("createdAt");

const pagination = computed<PaginationParams>(() => ({
  skip: (page.value - 1) * rowsPerPage,
  take: rowsPerPage,
  sortBy: sortBy.value,
  order: sortBy.value === "createdAt" ? "desc" : "asc",
}));

const filters = computed<Record<string, any>>(() => {
  return {};
});

const { data } = useGetReportTemplates(pagination, filters);

const templates = computed<ReportTemplate[]>(() => {
  return data.value?.templates ?? [];
});

const totalCount = computed<number>(() => {
  return data.value?.totalCount ?? 0;
});

const selectedTemplateId = ref<number | undefined>();
const selectTemplate = (template: ReportTemplate) => {
  selectedTemplateId.value = template.id;
};
const closeDrawer = () => {
  selectedTemplateId.value = undefined;
};

const showImportNewTemplate = ref(false);
const handleNewTemplate = () => {
  showImportNewTemplate.value = true;
};

const showDefaultSettingsDrawer = ref(false);
const handleOpenDefaultSettings = () => {
  showDefaultSettingsDrawer.value = true;
};
</script>

<template>
  <OrganizationPageLayout
    :title="t('dashboard:templates.list.title')"
    :description="t('dashboard:navigation.report-templates-description')"
  >
    <template #actions>
      <DRBtn
        :label="t('dashboard:templates.list.defaultSettingsButton')"
        icon="bi-gear"
        variant="outline"
        @click="handleOpenDefaultSettings"
      />
      <DRBtn
        :label="t('dashboard:templates.list.importButton')"
        icon="bi-upload"
        @click="handleNewTemplate"
      />
      <ImportTemplateDialog v-model="showImportNewTemplate" />
    </template>

    <DRDisplayList :data="templates" key-index="id">
      <template #item="{ item, display }">
        <TemplateItem
          :template="item"
          :display="display"
          @select="selectTemplate(item)"
        />
      </template>
      <template #empty>
        <CreateTemplateReportBanner />
      </template>
    </DRDisplayList>

    <div
      v-if="totalCount > rowsPerPage"
      class="d-flex justify-content-center mt-4"
    >
      <el-pagination
        v-model:current-page="page"
        :page-size="rowsPerPage"
        background
        layout="prev, pager, next"
        :total="totalCount"
      />
    </div>
  </OrganizationPageLayout>

  <TemplateDrawer :template-id="selectedTemplateId" @close="closeDrawer" />
  <TemplateDefaultSettingsDrawer v-model="showDefaultSettingsDrawer" />
</template>

<style lang="scss">
@import "@/style/variables.scss";
.dr-input {
  max-width: 300px;
}

.el-radio-button__inner {
  background-color: transparent !important;
  color: #80808097 !important;
  padding: 0 !important;
  width: 40px !important;
  height: 40px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-color: inherit;
}

.el-radio-button.is-active .el-radio-button__inner {
  background-color: white !important;
  color: $dark !important;
  border-color: #bebebe9d !important;
  box-shadow: none !important;
}
</style>
